import { Alert, Box, Collapse } from '@mui/material';
import { createPortal } from 'react-dom';

import { useToastContext } from './context/ToastContext';
import {
	FULLSCREEN_OVERLAY_Z_INDEX,
	useFullscreenContainer,
} from 'core/utils/fullscreenContainer';

/**
 * Stackable toast notifications rendered with MUI Alert.
 * Auto-dismiss is handled by {@link ToastContextProvider} timers.
 * Mounted once via {@link ToastContextProvider}.
 */
export default function Toast(): JSX.Element {
	const { toasts, closeToast } = useToastContext();
	const portalContainer = useFullscreenContainer();

	const content = (
		<Box
			aria-live="polite"
			aria-relevant="additions"
			sx={{
				position: 'fixed',
				bottom: (theme) => theme.spacing(3),
				left: (theme) => theme.spacing(3),
				zIndex: FULLSCREEN_OVERLAY_Z_INDEX,
				display: 'flex',
				flexDirection: 'column-reverse',
				gap: 1,
				alignItems: 'flex-start',
				width: '100%',
				maxWidth: (theme) => `min(420px, calc(100vw - ${theme.spacing(6)}))`,
				pointerEvents: 'none',
			}}
		>
			{toasts.map((toast) => (
				<Collapse key={toast.id} in>
					<Alert
						severity={toast.type}
						variant="filled"
						onClose={() => closeToast(toast.id)}
						sx={{
							pointerEvents: 'auto',
							width: '100%',
							boxShadow: 3,
						}}
					>
						{toast.message}
					</Alert>
				</Collapse>
			))}
		</Box>
	);

	if (portalContainer) {
		return createPortal(content, portalContainer);
	}

	return content;
}
